import React from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  formatCheckoutCurrency,
  SUBSCRIPTION_GST_PERCENT,
} from "../../Util/subscriptionTierHelper";
import { VM_Colors } from "../../Styles/VMStyles";

const formatMandateDate = (value) => dayjs(value).format("D MMMM YYYY");

const CheckoutGstConfirmModal = ({
  open,
  onClose,
  onConfirm,
  planDisplayName = "",
  billingLabel = "",
  breakdown,
  currency = "INR",
  taxLabel,
  planPeriod = "Monthly",
}) => {
  const totalLabel = formatCheckoutCurrency(breakdown?.totalAmount, currency);
  const baseLabel = formatCheckoutCurrency(breakdown?.baseAmount, currency);
  const gstLabel = formatCheckoutCurrency(breakdown?.gstAmount, currency);
  const gstPercent = breakdown?.gstPercent ?? SUBSCRIPTION_GST_PERCENT;
  const gstRowLabel = taxLabel || `Tax / GST (${gstPercent}%)`;
  const accessLabel = billingLabel
    ? `${billingLabel} access`
    : `${planPeriod} access`;
  const frequencyLabel = planPeriod === "Yearly" ? "Yearly" : "Monthly";
  const start = dayjs();
  const validityLabel = `${formatMandateDate(start)} to ${formatMandateDate(
    start.add(10, "year")
  )}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 0, overflow: "hidden" },
      }}
    >
      <DialogContent sx={{ px: 2.5, pt: 3, pb: 1.5 }}>
        <Typography
          align="center"
          sx={{ fontWeight: 800, fontSize: "1.2rem", color: "#0F172A", mb: 2 }}
        >
          Autopay details
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2.5 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: VM_Colors.turquoise,
              fontWeight: 800,
              fontSize: 22,
              mb: 1,
            }}
          >
            V
          </Avatar>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0F172A" }}>
            Venue Manager
          </Typography>
          {(planDisplayName || accessLabel) && (
            <Typography
              sx={{
                mt: 0.35,
                fontWeight: 600,
                fontSize: 13,
                color: VM_Colors.turquoise,
              }}
            >
              {[planDisplayName, accessLabel].filter(Boolean).join(" · ")}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            border: `1px solid ${VM_Colors.light_gray}`,
            borderRadius: 2.5,
            overflow: "hidden",
            mb: 1.5,
          }}
        >
          <DetailBlock label="First payment" value={totalLabel || "—"} />
          <Divider />
          <DetailBlock
            label="Payment Limit"
            value={totalLabel ? `Up to ${totalLabel}` : "—"}
          />
          <Divider />
          <DetailBlock
            label="Frequency"
            value="As presented"
            subtitle={frequencyLabel}
          />
          <Divider />
          <DetailBlock label="Validity" value={validityLabel} />
          <Divider />
          <DetailBlock label="Note" value="Autopay" />
        </Box>

        <Box
          sx={{
            borderRadius: 2.5,
            bgcolor: VM_Colors.white1,
            p: 2,
          }}
        >
          <SummaryRow label="Subscription amount" value={baseLabel || "—"} />
          <SummaryRow
            label={gstRowLabel}
            value={gstLabel ? `+ ${gstLabel}` : "—"}
          />
          <Divider sx={{ my: 1.25 }} />
          <SummaryRow
            label="Starting today"
            value={totalLabel || "—"}
            emphasize
          />
        </Box>

        <Typography
          align="center"
          sx={{
            display: "block",
            mt: 1.75,
            fontSize: 12,
            color: "#94A3B8",
            lineHeight: 1.5,
          }}
        >
          You can pause or cancel this Autopay anytime
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, gap: 1.25 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            py: 1.15,
            borderColor: VM_Colors.light_gray,
            color: "#0F172A",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          fullWidth
          disabled={!breakdown}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            py: 1.15,
            bgcolor: VM_Colors.primary,
            "&:hover": { bgcolor: VM_Colors.primary, filter: "brightness(0.92)" },
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DetailBlock = ({ label, value, subtitle }) => (
  <Box sx={{ px: 2, py: 1.5 }}>
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 500,
        color: "#94A3B8",
        mb: 0.35,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0F172A", lineHeight: 1.35 }}>
      {value}
    </Typography>
    {subtitle ? (
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#64748B", mt: 0.2 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

const SummaryRow = ({ label, value, emphasize = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      py: 0.35,
    }}
  >
    <Typography
      sx={{
        fontSize: emphasize ? 15 : 14,
        fontWeight: emphasize ? 700 : 500,
        color: emphasize ? "#0F172A" : "#64748B",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: emphasize ? 18 : 14,
        fontWeight: emphasize ? 800 : 600,
        color: "#0F172A",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default CheckoutGstConfirmModal;
